import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    InputAdornment,
    Stack,
    Select,
    MenuItem,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MyReviewCard from './MyReviewCard';
import EmptyState from './EmptyState';
import ReviewDetailModal from './ReviewDetailModal';
import { useGetMyReviews, useGetProjectTrackerProjects, useWithdrawTaskSubmission } from '../../../services/api/task-management/trackerHooks';
import { format } from 'date-fns';
import { CARDS_PER_PAGE } from 'module/task-management/constants'
import { TASK_STATUS } from '../helper/taskStatuses';

const sortSubmissionsByNumber = (submissions = []) => (
    [...submissions].sort((a, b) => a.submission_number - b.submission_number)
);

const sortReviewsByDate = (reviews = []) => (
    [...reviews].sort((a, b) => new Date(a.reviewed_at) - new Date(b.reviewed_at))
);

const hasSubmissionLink = (review) => (
    review?.task_submission_id !== null && review?.task_submission_id !== undefined
);

const buildSubmissionReviewResolver = (submissions = [], reviews = []) => {
    const sortedSubmissions = sortSubmissionsByNumber(submissions);
    const sortedReviews = sortReviewsByDate(reviews);

    const reviewsBySubmissionId = new Map(
        sortedReviews
            .filter(hasSubmissionLink)
            .map((item) => [String(item.task_submission_id), item])
    );

    const directlyLinkedSubmissionIds = new Set(
        sortedReviews
            .filter(hasSubmissionLink)
            .map((item) => String(item.task_submission_id))
    );

    const legacyReviews = sortedReviews.filter((item) => !hasSubmissionLink(item));
    const legacyReviewableSubmissions = sortedSubmissions.filter((submission) => {
        const hasDirectReview =
            Boolean(submission.review) ||
            directlyLinkedSubmissionIds.has(String(submission.id));

        return !submission.withdrawn_at && !hasDirectReview;
    });

    const legacyReviewsBySubmissionId = new Map();

    legacyReviewableSubmissions.forEach((submission, index) => {
        const legacyReview = legacyReviews[index];

        if (legacyReview) {
            legacyReviewsBySubmissionId.set(String(submission.id), legacyReview);
        }
    });

    const resolveSubmissionReview = (submission) => {
        if (!submission) return null;

        return (
            submission.review ||
            reviewsBySubmissionId.get(String(submission.id)) ||
            legacyReviewsBySubmissionId.get(String(submission.id)) ||
            null
        );
    };

    return {
        sortedSubmissions,
        sortedReviews,
        resolveSubmissionReview
    };
};

const MyReviewsTab = ({ onPaginationChange, setSuccessMessage, setErrorMessage }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(CARDS_PER_PAGE);
    const [selectedReview, setSelectedReview] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
    const [reviewToWithdraw, setReviewToWithdraw] = useState(null);

    // Fetch my reviews from API
    const { data: reviewsData, isLoading: isLoadingReviews, error: reviewsError } = useGetMyReviews();

    // Fetch projects to get project names
    const { data: projectsData } = useGetProjectTrackerProjects();
    const { mutate: withdrawTaskSubmission, isLoading: isWithdrawing } = useWithdrawTaskSubmission();

    // Transform API response to match UI structure
    const transformedReviews = useMemo(() => {
        if (!reviewsData?.data?.reviews) return [];

        const reviews = reviewsData.data.reviews;

        // Create project map for quick lookup
        const projectMap = {};
        if (projectsData?.data?.projects) {
            projectsData.data.projects.forEach(project => {
                projectMap[project.id] = project.name;
            });
        }

        const result = [];

        reviews.forEach(review => {
            const projectName = projectMap[review.project_id] || `Project ${review.project_id}`;
            const {
                sortedSubmissions,
                sortedReviews,
                resolveSubmissionReview
            } = buildSubmissionReviewResolver(review.submissions || [], review.reviews || []);

            if (sortedSubmissions.length === 0) {
                const legacyReview = review.review || sortedReviews[sortedReviews.length - 1] || null;

                if (legacyReview) {
                    result.push({
                    id: `${review.id}-legacy`,
                        taskId: review.id,
                        projectId: review.project_id,
                        projectName,
                        taskName: review.name,
                        count: parseFloat(review.value),
                        status: 'reviewed',
                        rating: legacyReview.rating || null,
                        submissionNumber: null,
                        totalSubmissions: 0,
                        managerName: review.reviewer?.name || 'Auto-approved',
                        managerAvatar:
                            review.reviewer?.profile_image_url ||
                            review.reviewer?.staff_profile_image?.profile_image_url ||
                            null,
                        reviewedDate: legacyReview.reviewed_at
                            ? format(new Date(legacyReview.reviewed_at), "MMM dd yyyy 'at' h:mm a")
                            : null,
                        submittedDate: review.submitted_at
                            ? format(new Date(review.submitted_at), "MMM dd yyyy 'at' h:mm a")
                            : null,
                        withdrawnAt: null,
                        autoApproved: review.auto_approved,
                        reviewData: legacyReview,
                        deliverableType: review.project_member_deliverable?.deliverable_type,
                        targetValue: review.project_member_deliverable?.target_value,
                        rawData: {
                            ...review,
                            _currentSubmission: null,
                            _currentReview: legacyReview,
                            _allSubmissions: sortedSubmissions,
                            _allReviews: sortedReviews
                        }
                    });
                }

                return;
            }

            // Only create cards based on actual entries in submissions[]
            // Never derive extra cards from task-level status changes
            sortedSubmissions.forEach((submission, index) => {
                const correspondingReview = resolveSubmissionReview(submission);
                const isLatest = index === sortedSubmissions.length - 1;
                const isWithdrawn = Boolean(submission.withdrawn_at);

                let uiStatus = 'pending';
                let rating = null;

                if (isWithdrawn) {
                    uiStatus = 'withdrawn';
                } else if (correspondingReview) {
                    uiStatus = 'reviewed';
                    rating = correspondingReview.rating;
                } else if (review.auto_approved && isLatest) {
                    uiStatus = 'reviewed';
                    rating = 'thumbs_up';
                } else if (isLatest) {
                    if (review.status === TASK_STATUS.SUBMITTED_FOR_REVIEW || review.status === TASK_STATUS.PENDING) {
                        uiStatus = 'pending';
                    } else if (review.status === TASK_STATUS.COMPLETED) {
                        uiStatus = 'reviewed';
                        rating = review.review?.rating || 'thumbs_up';
                    } else if (review.status === TASK_STATUS.RETURNED_FOR_REWORK) {
                        uiStatus = 'reviewed';
                    }
                } else {
                    uiStatus = 'pending';
                }

                result.push({
                    id: `${review.id}-sub-${submission.submission_number}`,
                    taskId: review.id,
                    projectId: review.project_id,
                    projectName,
                    taskName: review.name,
                    count: parseFloat(review.value),
                    status: uiStatus,
                    rating,
                    submissionNumber: submission.submission_number,
                    totalSubmissions: sortedSubmissions.length,
                    managerName: (isWithdrawn && submission.reviewer?.name)
                        ? submission.reviewer.name
                        : (isLatest && (review.auto_approved || review.reviewer_id === null))
                        ? 'Auto-approved'
                        : (submission.reviewer?.name || review.reviewer?.name || 'Auto-approved'),
                    managerAvatar: (isWithdrawn && submission.reviewer?.profile_image_url)
                        ? submission.reviewer.profile_image_url
                        : (isLatest && (review.auto_approved || review.reviewer_id === null))
                        ? null
                        : (
                            submission.reviewer?.profile_image_url ||
                            review.reviewer?.profile_image_url ||
                            review.reviewer?.staff_profile_image?.profile_image_url ||
                            null
                        ),
                    reviewedDate: correspondingReview?.reviewed_at
                        ? format(new Date(correspondingReview.reviewed_at), "MMM dd yyyy 'at' h:mm a")
                        : null,
                    // Use the submission's own submitted_at, not the task-level date
                    submittedDate: submission.submitted_at
                        ? format(new Date(submission.submitted_at), "MMM dd yyyy 'at' h:mm a")
                        : null,
                    withdrawnAt: submission.withdrawn_at
                        ? format(new Date(submission.withdrawn_at), "MMM dd yyyy 'at' h:mm a")
                        : null,
                    autoApproved: review.auto_approved,
                    reviewData: correspondingReview,
                    deliverableType: review.project_member_deliverable?.deliverable_type,
                    targetValue: review.project_member_deliverable?.target_value,
                    rawData: {
                        ...review,
                        _currentSubmission: submission,
                        _currentReview: correspondingReview,
                        _allSubmissions: sortedSubmissions,
                        _allReviews: sortedReviews
                    }
                });
            });
        });

        return result;
    }, [reviewsData, projectsData]);

    console.log('🔍 Reviews API Data:', reviewsData);
    console.log('🔍 Transformed Reviews:', transformedReviews);

    // Use transformed data
    const myReviews = transformedReviews;

    useEffect(() => {
        setPage(0)
    }, [reviewsData, projectsData, searchQuery, sortBy])

    // Filter reviews by search query
    const filteredReviews = useMemo(() => {
        if (!searchQuery) return myReviews;

        return myReviews.filter(review =>
            review.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            review.taskName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            review.managerName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery, myReviews]);

    // Sort reviews based on sortBy selection
    const sortedReviews = useMemo(() => {
        if (!filteredReviews || filteredReviews.length === 0) return [];

        const sorted = [...filteredReviews]; // Make a copy to avoid mutating original

        sorted.sort((a, b) => {
            // Sort by the individual submission's submitted_at, not the task-level date
            const dateA = a.rawData._currentSubmission?.submitted_at || a.rawData.submitted_at;
            const dateB = b.rawData._currentSubmission?.submitted_at || b.rawData.submitted_at;

            // Convert to timestamps for comparison
            const timeA = dateA ? new Date(dateA).getTime() : 0;
            const timeB = dateB ? new Date(dateB).getTime() : 0;

            if (sortBy === 'newest') {
                // Newest first: bigger timestamp comes first (descending)
                return timeB - timeA;
            } else if (sortBy === 'oldest') {
                // Oldest first: smaller timestamp comes first (ascending)
                return timeA - timeB;
            }

            return 0; // No sorting if sortBy doesn't match
        });

        return sorted;
    }, [filteredReviews, sortBy]);

    // Group reviews by manager, then by project
    const groupedByManager = useMemo(() => {
        const grouped = {};

        sortedReviews.forEach(review => {
            const managerKey = review.managerName || 'Unknown';

            if (!grouped[managerKey]) {
                grouped[managerKey] = {
                    managerName: review.managerName,
                    managerAvatar: review.managerAvatar,
                    projects: []
                };
            }

            // Find or create project group
            let projectGroup = grouped[managerKey].projects.find(p => p.projectName === review.projectName);

            if (!projectGroup) {
                projectGroup = {
                    projectName: review.projectName,
                    projectId: review.projectId,
                    reviews: []
                };
                grouped[managerKey].projects.push(projectGroup);
            }

            projectGroup.reviews.push(review);
        });

        // Convert to array and sort by manager name
        const managersArray = Object.values(grouped).sort((a, b) =>
            a.managerName.localeCompare(b.managerName)
        );

        return managersArray;
    }, [sortedReviews]);

    // Paginate grouped data
    const paginatedGroups = useMemo(() => {
        const startIndex = page * rowsPerPage;
        return groupedByManager.slice(startIndex, startIndex + rowsPerPage);
    }, [groupedByManager, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Notify parent component of pagination state
    React.useEffect(() => {
        if (onPaginationChange) {
            onPaginationChange({
                page,
                rowsPerPage,
                totalCount: groupedByManager.length,
                onPageChange: handleChangePage
            });
        }
    }, [page, rowsPerPage, groupedByManager.length, onPaginationChange]);

    const handleViewFeedback = (review) => {
        const rawData = review.rawData;

        // Use per-submission data stored on rawData by transformedReviews
        const allSubmissions = rawData._allSubmissions || sortSubmissionsByNumber(rawData.submissions || []);
        const allReviews = rawData._allReviews || sortReviewsByDate(rawData.reviews || []);
        const { resolveSubmissionReview } = buildSubmissionReviewResolver(allSubmissions, allReviews);

        // This card's specific submission and review
        const currentSubmission = rawData._currentSubmission || allSubmissions[allSubmissions.length - 1] || null;
        const currentReview = currentSubmission
            ? (rawData._currentReview || resolveSubmissionReview(currentSubmission))
            : (rawData._currentReview || rawData.review || allReviews[allReviews.length - 1] || null);

        // Build full submission history for the modal timeline
        const submissionHistory = allSubmissions.map((submission) => ({
            submissionNumber: submission.submission_number,
            submittedAt: submission.submitted_at,
            withdrawnAt: submission.withdrawn_at || null,
            remarks: submission.submission_remarks || '',
            link: submission.submission_link || '',
            attachments: submission.attachments || [],
            review: resolveSubmissionReview(submission)
        }));

        const modalData = {
            taskName: rawData.name,
            projectName: review.projectName,
            submissionNumber: currentSubmission?.submission_number,
            totalSubmissions: allSubmissions.length,
            reviewedBy: currentSubmission?.reviewer?.name || rawData.reviewer?.name || 'Auto-approved',
            reviewedDate: currentReview?.reviewed_at
                ? format(new Date(currentReview.reviewed_at), "MMM dd yyyy 'at' h:mm a")
                : null,
            rating: currentReview?.rating,
            feedback: currentReview?.feedback,
            remarks: currentSubmission?.submission_remarks || '',
            submissionLink: currentSubmission?.submission_link || '',
            attachments: currentSubmission?.attachments || [],
            submissions: rawData.submissions || [],
            latestSubmission: currentSubmission,
            submissionHistory: submissionHistory
        };

        setSelectedReview(modalData);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedReview(null);
    };

    const handleWithdrawClick = (review) => {
        setReviewToWithdraw(review);
        setWithdrawConfirmOpen(true);
    };

    const handleCloseWithdrawDialog = () => {
        setWithdrawConfirmOpen(false);
        setReviewToWithdraw(null);
    };

    const handleConfirmWithdraw = () => {
        if (!reviewToWithdraw?.taskId) return;

        withdrawTaskSubmission(reviewToWithdraw.taskId, {
            onSuccess: (data) => {
                const successMsg =
                    data?.message || 'Task submission withdrawn successfully. You can edit and resubmit it now.';

                setSuccessMessage?.(successMsg);
                setTimeout(() => setSuccessMessage?.(null), 5000);
                handleCloseWithdrawDialog();
            },
            onError: (error) => {
                const errorMsg =
                    error?.response?.data?.message || 'Failed to withdraw task submission. Please try again.';

                setErrorMessage?.(errorMsg);
                setTimeout(() => setErrorMessage?.(null), 5000);

                const status = error?.response?.status;
                if (status === 404 || status === 409) {
                    handleCloseWithdrawDialog();
                }
            }
        });
    };

    // Loading state
    if (isLoadingReviews) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    // Error state
    if (reviewsError) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Typography color="error">Failed to load reviews. Please try again.</Typography>
            </Box>
        );
    }

    // Empty state
    if (myReviews.length === 0) {
        return (
            <Box>
                <EmptyState type="my-reviews" />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header Section */}
            <Box display="flex" justifyContent="space-between" mb={3}>
                <Box>
                    <Typography variant="h3" fontWeight={500} mb={0.5} color="#000000">
                        My Reviews
                    </Typography>
                    <Typography variant="caption" color="#000000">
                        Track task submissions and performance feedback.
                    </Typography>
                </Box>

                {/* Filter and Search Bar */}
                {myReviews.length > 0 && (
                    <Box display="flex" alignItems="center" gap={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                size="small"
                                sx={{
                                    borderRadius: '50px',
                                    minWidth: '180px',
                                    backgroundColor: '#f9f9f9',
                                    outline: 'none !important',
                                    '& .MuiSelect-select': {
                                        outline: 'none !important',
                                        '&:focus': {
                                            outline: 'none !important',
                                            backgroundColor: '#f9f9f9'
                                        }
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#8A8A8A !important',
                                        borderWidth: '1px !important',
                                        outline: 'none !important'
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: 'none !important',
                                        outline: 'none !important'
                                    },
                                    '&:focus': {
                                        outline: 'none !important'
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important'
                                    },
                                    '&:focus-within': {
                                        outline: 'none !important'
                                    }
                                }}
                                renderValue={(value) => (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <EventIcon sx={{ fontSize: 20 }} />
                                        {value === 'newest' ? 'Newest First' : 'Oldest First'}
                                    </Box>
                                )}
                            >
                                <MenuItem value="newest">Newest First</MenuItem>
                                <MenuItem value="oldest">Oldest First</MenuItem>
                            </Select>
                        </Stack>

                        <TextField
                            size="small"
                            placeholder="Search projects or tasks"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#3a3a3a' }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                width: '250px',
                                outline: 'none !important',
                                '& .MuiOutlinedInput-root': {
                                    borderColor: '#3a3a3a',
                                    borderRadius: '100px',
                                    color: '#3a3a3a',
                                    outline: 'none !important',
                                    '& fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        borderWidth: '1px !important',
                                        outline: 'none !important'
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: 'none !important',
                                        outline: 'none !important'
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important'
                                    }
                                },
                                '& input': {
                                    outline: 'none !important',
                                    '&:focus': {
                                        outline: 'none !important',
                                        boxShadow: 'none !important'
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important'
                                    }
                                },
                                '&:focus-within': {
                                    outline: 'none !important'
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>

            {/* Grid of Review Cards */}
            <Grid container spacing={3}>
                {paginatedGroups.map((group) => (
                    <Grid item xs={12} sm={6} md={4} key={group.managerName}>
                        <MyReviewCard
                            managerData={{
                                name: group.managerName,
                                avatar: group.managerAvatar
                            }}
                            projects={group.projects}
                            onViewFeedback={handleViewFeedback}
                            onWithdrawSubmission={handleWithdrawClick}
                            withdrawingReviewId={reviewToWithdraw?.id}
                        />
                    </Grid>
                ))}
            </Grid>

            <Dialog
                open={withdrawConfirmOpen}
                onClose={handleCloseWithdrawDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, p: 2 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 3 }}>
                    <WarningAmberIcon sx={{ fontSize: 56, color: 'warning.main' }} />
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#333', mt: 1 }}>
                        Withdraw submission?
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ textAlign: 'center', px: 4 }}>
                    <Typography variant="body1" sx={{ color: '#555', fontSize: '1.1rem', lineHeight: 1.5 }}>
                        This will remove the task from the reviewer&apos;s pending list and restore the task to
                        its previous status so you can edit and submit it again.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ flexDirection: 'column', gap: 1.5, px: 4, pb: 4 }}>
                    <Button
                        fullWidth
                        onClick={handleConfirmWithdraw}
                        variant="contained"
                        disabled={isWithdrawing}
                        sx={{
                            backgroundColor: 'warning.main',
                            color: '#000',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '14px',
                            borderRadius: '100px',
                            py: 1.5,
                            '&:hover': { backgroundColor: '#D09A01' }
                        }}
                    >
                        {isWithdrawing ? 'Withdrawing...' : 'Withdraw'}
                    </Button>

                    <Button
                        fullWidth
                        onClick={handleCloseWithdrawDialog}
                        disabled={isWithdrawing}
                        sx={{
                            color: '#333',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '1rem',
                            py: 1
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Review Detail Modal */}
            <ReviewDetailModal
                open={isModalOpen}
                onClose={handleCloseModal}
                reviewData={selectedReview}
            />
        </Box>
    );
};

export default MyReviewsTab;
